import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  useTheme,
  Menu,
  TablePagination,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataElement, dataElementsService, SelectionOption } from '../../services/dataelements-service';
import AddDataElementPanel from './AddDataElementPanel';
import EditDataElementPanel from './EditDataElementPanel';
import { Global } from '@emotion/react';

const DataElements: React.FC = () => {
  const theme = useTheme();
  const [sortBy, setSortBy] = useState('A-Z');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dataElements, setDataElements] = useState<DataElement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [elementToDelete, setElementToDelete] = useState<DataElement | null>(null);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [selectedElement, setSelectedElement] = useState<DataElement | null>(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    isPublished: 'ALL',
    type: 'ALL',
    isIdentifier: 'ALL',
    isPersonalData: 'ALL'
  });
  const [totalCount, setTotalCount] = useState(0);

  // Reset panel states when component mounts
  useEffect(() => {
    setIsEditPanelOpen(false);
    setIsAddPanelOpen(false);
    setSelectedElement(null);
  }, []);

  const fetchDataElements = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dataElementsService.getDataElements(
        {
          isPublished: filters.isPublished === 'ALL' ? undefined : filters.isPublished === 'PUBLISHED',
          typeId: filters.type === 'ALL' ? undefined : dataElements.find(e => e.type?.typeName === filters.type)?.typeId,
          isIdentifier: filters.isIdentifier === 'ALL' ? undefined : filters.isIdentifier === 'TRUE',
          isPersonalData: filters.isPersonalData === 'ALL' ? undefined : filters.isPersonalData === 'TRUE'
        },
        {
          page: page + 1, // Convert to 1-based index for server
          pageSize: rowsPerPage
        },
        sortBy
      );
      setDataElements(response.elements);
      setTotalCount(response.total);
    } catch (error) {
      console.error('Error fetching data elements:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch data elements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDataElements();
  }, [page, rowsPerPage, sortBy, filters]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing rows per page
  };

  const handleSortChange = (newSortBy: string) => {
    setSortBy(newSortBy);
    setPage(0); // Reset to first page when changing sort
  };

  const handleFilterChange = (field: keyof typeof filters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0); // Reset to first page when changing filters
  };

  const handleApplyFilters = () => {
    setPage(0); // Reset to first page when applying filters
    setIsFilterModalOpen(false);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(dataElements.map(element => element.elementId!));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelect = (elementId: number) => {
    setSelectedItems(prev =>
      prev.includes(elementId)
        ? prev.filter(item => item !== elementId)
        : [...prev, elementId]
    );
  };

  const handleDelete = async (elementId: number) => {
    const element = dataElements.find(e => e.elementId === elementId);
    if (element) {
      setElementToDelete(element);
      setDeleteDialogOpen(true);
    }
  };

  const handleUndelete = async (elementId: number) => {
    setDataElements(prev => prev.map(e => e.elementId === elementId ? { ...e, isPublished: true } : e));
  };

  const confirmDelete = async () => {
    if (elementToDelete) {
      setDataElements(prev => prev.map(e => e.elementId === elementToDelete.elementId ? { ...e, isPublished: false } : e));
      setDeleteDialogOpen(false);
      setElementToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setElementToDelete(null);
  };

  const getStatusColor = (isPublished: boolean) => {
    return isPublished ? theme.palette.success.main : theme.palette.error.light;
  };

  const renderOptions = (options?: SelectionOption[]) => {
    if (!options || options.length === 0) return null;

    return (
      <Tooltip title={
        <Box>
          {options.map((option, index) => (
            <div key={index}>{option.optionLabel}</div>
          ))}
        </Box>
      }>
        <Chip
          label={options.length}
          size="small"
          sx={{
            bgcolor: theme.palette.neutral.main,
            color: theme.palette.neutral.contrastText
          }}
        />
      </Tooltip>
    );
  };

  const hasActiveFilters = () => {
    return filters.isPublished !== 'ALL' || 
           filters.type !== 'ALL' || 
           filters.isIdentifier !== 'ALL' || 
           filters.isPersonalData !== 'ALL';
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        color: theme.palette.text.primary
      }}>
        Loading data elements...
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{
        mb: theme.custom.titleSpacing.marginBottom,
        color: theme.palette.text.primary
      }}>
        Data Elements
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {selectedItems.length > 0 && (
            <>
              {selectedItems.length > 0 && selectedItems.every(id => {
                const el = dataElements.find(e => e.elementId === id);
                return el && el.isPublished;
              }) && (
              <IconButton
                onClick={() => selectedItems.forEach(id => handleDelete(id))}
                sx={{ color: theme.palette.text.primary }}
              >
                <DeleteIcon />
              </IconButton>
              )}
              {selectedItems.length > 0 && selectedItems.every(id => {
                const el = dataElements.find(e => e.elementId === id);
                return el && !el.isPublished;
              }) && (
                <IconButton
                  onClick={() => selectedItems.forEach(id => handleUndelete(id))}
                  sx={{ color: theme.palette.text.primary }}
                >
                  <RestoreFromTrashIcon />
                </IconButton>
              )}
              {selectedItems.length === 1 && (
                <IconButton sx={{ color: theme.palette.text.primary }} onClick={() => {
                  const element = dataElements.find(e => e.elementId === selectedItems[0]);
                  setSelectedElement(element || null);
                  setIsEditPanelOpen(true);
                }}>
                  <EditIcon />
                </IconButton>
              )}
            </>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Global styles={`
            @keyframes spin {
              100% { transform: rotate(360deg); }
            }
          `} />
          <IconButton
            onClick={fetchDataElements}
            sx={{ color: theme.palette.text.primary }}
            disabled={loading}
          >
            <RefreshIcon
              sx={{
                transition: 'transform 0.3s',
                animation: loading ? 'spin 1s linear infinite' : undefined
              }}
            />
          </IconButton>
          <Select
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
            size="small"
            sx={{ width: 150 }}
          >
            <MenuItem value="A-Z">A-Z</MenuItem>
            <MenuItem value="Z-A">Z-A</MenuItem>
            <MenuItem value="newest">Newest</MenuItem>
            <MenuItem value="oldest">Oldest</MenuItem>
          </Select>
          <IconButton 
            onClick={() => setIsFilterModalOpen(true)}
            sx={{ 
              color: theme.palette.text.primary,
              position: 'relative',
              '&::after': hasActiveFilters() ? {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: theme.palette.primary.main,
                border: `2px solid ${theme.palette.background.paper}`,
              } : {}
            }}
          >
            <FilterListIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsAddPanelOpen(true)}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                bgcolor: theme.palette.primary.dark
              }
            }}
          >
            ADD NEW
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: theme.palette.text.primary }}>Name</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>Code</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>Type</TableCell>
              
              <TableCell sx={{ color: theme.palette.text.primary }}>PII</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>Identifier</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>Options</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>Created on</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dataElements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ borderBottom: 'none' }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 3,
                        color: theme.palette.text.secondary,
                        textAlign: 'center'
                      }}
                    >
                      There are no data elements to display. Click the button below to create your first data element
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setIsAddPanelOpen(true)}
                      sx={{ 
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        '&:hover': { 
                          bgcolor: theme.palette.primary.dark 
                        }
                      }}
                    >
                      ADD NEW
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              dataElements.map((element) => (
                <TableRow
                  key={element.elementId}
                  hover
                  sx={{
                    '&.MuiTableRow-root:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <TableCell 
                    sx={{ 
                      color: theme.palette.text.primary,
                      cursor: 'pointer',
                      '&:hover': {
                        color: theme.palette.primary.main
                      }
                    }}
                    onClick={() => {
                      setSelectedElement(element);
                      setIsEditPanelOpen(true);
                    }}
                  >
                    {element.elementName}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>
                    {element.elementCode}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={element.type?.description || ''}>
                      <Chip
                        label={element.type?.typeName || element.typeId}
                        size="small"
                        sx={{
                          bgcolor: theme.palette.neutral.main,
                          color: theme.palette.neutral.contrastText
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {element.isPersonalData ? (
                      <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                    ) : (
                      <CancelIcon sx={{ color: theme.palette.neutral.main }} />
                    )}
                  </TableCell>
                  <TableCell>
                    {element.isIdentifier ? (
                      <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                    ) : (
                      <CancelIcon sx={{ color: theme.palette.neutral.main }} />
                    )}
                  </TableCell>
                  <TableCell>{renderOptions(element.options)}</TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>{element.createdDate ? new Date(element.createdDate).toLocaleDateString('en-US', { timeZone: 'UTC' }) : ''}</TableCell>
                  <TableCell>
                    <Chip
                      label={element.isPublished ? 'Published' : 'Draft'}
                      size="small"
                      sx={{
                        bgcolor: getStatusColor(element.isPublished),
                        color: 'white',
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: theme.palette.text.primary,
            '& .MuiTablePagination-selectIcon': {
              color: theme.palette.text.primary,
            },
          }}
        />
      </TableContainer>

      <AddDataElementPanel 
        open={isAddPanelOpen} 
        onClose={() => setIsAddPanelOpen(false)}
        onSave={fetchDataElements}
      />

      {isEditPanelOpen && (
        <EditDataElementPanel 
          open={isEditPanelOpen} 
          onClose={() => {
            setIsEditPanelOpen(false);
            setSelectedElement(null);
          }} 
          selectedElement={selectedElement} 
          onSave={fetchDataElements}
        />
      )}

      <Dialog open={deleteDialogOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to mark this data element as inactive?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="primary">Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle>Filters</DialogTitle>
        <Divider sx={{ borderColor: theme.palette.divider }} />
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <FormControl>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  mb: 1
                }}
              >
                Published Status
              </Typography>
              <RadioGroup
                value={filters.isPublished}
                onChange={(e) => handleFilterChange('isPublished', e.target.value)}
                row
              >
                <FormControlLabel 
                  value="ALL" 
                  control={<Radio sx={{ color: theme.palette.text.secondary }} />} 
                  label="All" 
                  sx={{ color: theme.palette.text.primary }}
                />
                <FormControlLabel 
                  value="PUBLISHED" 
                  control={<Radio sx={{ color: theme.palette.text.secondary }} />} 
                  label="Published" 
                  sx={{ color: theme.palette.text.primary }}
                />
                <FormControlLabel 
                  value="DRAFT" 
                  control={<Radio sx={{ color: theme.palette.text.secondary }} />} 
                  label="Draft" 
                  sx={{ color: theme.palette.text.primary }}
                />
              </RadioGroup>
            </FormControl>

            <FormControl>
              <InputLabel sx={{ color: theme.palette.text.secondary }}>Type</InputLabel>
              <Select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                label="Type"
                sx={{
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value="ALL" sx={{ color: theme.palette.text.primary }}>All Types</MenuItem>
                {dataElements
                  .map(element => element.type?.typeName)
                  .filter((type, index, self) => type && self.indexOf(type) === index)
                  .map(type => (
                    <MenuItem key={type} value={type} sx={{ color: theme.palette.text.primary }}>
                      {type}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  mb: 1
                }}
              >
                Identifier
              </Typography>
              <RadioGroup
                value={filters.isIdentifier}
                onChange={(e) => handleFilterChange('isIdentifier', e.target.value)}
                row
              >
                <FormControlLabel 
                  value="ALL" 
                  control={<Radio sx={{ color: theme.palette.text.secondary }} />} 
                  label="All" 
                  sx={{ color: theme.palette.text.primary }}
                />
                <FormControlLabel 
                  value="TRUE" 
                  control={<Radio sx={{ color: theme.palette.text.secondary }} />} 
                  label="Yes" 
                  sx={{ color: theme.palette.text.primary }}
                />
                <FormControlLabel 
                  value="FALSE" 
                  control={<Radio sx={{ color: theme.palette.text.secondary }} />} 
                  label="No" 
                  sx={{ color: theme.palette.text.primary }}
                />
              </RadioGroup>
            </FormControl>

            <FormControl>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  mb: 1
                }}
              >
                PII
              </Typography>
              <RadioGroup
                value={filters.isPersonalData}
                onChange={(e) => handleFilterChange('isPersonalData', e.target.value)}
                row
              >
                <FormControlLabel 
                  value="ALL" 
                  control={<Radio sx={{ color: theme.palette.text.secondary }} />} 
                  label="All" 
                  sx={{ color: theme.palette.text.primary }}
                />
                <FormControlLabel 
                  value="TRUE" 
                  control={<Radio sx={{ color: theme.palette.text.secondary }} />} 
                  label="Yes" 
                  sx={{ color: theme.palette.text.primary }}
                />
                <FormControlLabel 
                  value="FALSE" 
                  control={<Radio sx={{ color: theme.palette.text.secondary }} />} 
                  label="No" 
                  sx={{ color: theme.palette.text.primary }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <Divider sx={{ borderColor: theme.palette.divider }} />
        <DialogActions>
          <Button 
            onClick={() => setIsFilterModalOpen(false)}
            sx={{ color: theme.palette.text.primary }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              setFilters({
                isPublished: 'ALL',
                type: 'ALL',
                isIdentifier: 'ALL',
                isPersonalData: 'ALL'
              });
            }}
            disabled={!hasActiveFilters()}
            variant="outlined"
            sx={{ 
              color: theme.palette.text.primary,
              borderColor: theme.palette.divider,
              '&:hover': {
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                bgcolor: 'transparent'
              },
              '&.Mui-disabled': {
                color: theme.palette.action.disabled,
                borderColor: theme.palette.divider
              }
            }}
          >
            Reset Filters
          </Button>
          <Button 
            variant="contained" 
            onClick={handleApplyFilters}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataElements; 